"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.SyftClient = void 0;
const SymbolMetadata_1 = require("./models/SymbolMetadata");
const Locale_1 = require("./enums/Locale");
const api = __importStar(require("./helpers/api"));
const url_1 = require("./helpers/url");
const SymbolUniverse_1 = require("./models/SymbolUniverse");
const ApiResponse_1 = require("./models/ApiResponse");
const SymbolList_1 = require("./models/SymbolList");
const IndexList_1 = require("./models/IndexList");
/**
 * The Syft Client class. Provides methods to access the Syft API.
 *
 * @export
 * @class SyftClient
 */
class SyftClient {
    /**
     *
     * @param syftUrl - The URL of the syft endpoint
     * @param locale - When used applies to all api requests. Specifies the response language used for localizable fields in the symbol metadata, such as description. This parameter must be a standard language identifier, which consists of a two-letter language identifier combined with a two-letter region identifier, such as en-US or ja-JP. Not all symbols support all languages. Requesting an unsupported language will cause the response to fall back to en-US, which is supported for all symbols.
     */
    constructor(syftUrl, locale) {
        this._syftUrl = syftUrl;
        this._language = this.getLanguageString(locale !== null && locale !== void 0 ? locale : Locale_1.Locale.Japanese);
    }
    /**
     * Gets a list of symbols from the symbol universe
     * @privateRemarks https://confluence.tradestation.com/display/MI/Syft+API+Specification#SyftAPISpecification-GET/symbols/
     * @privateRemarks GET /symbols/
     *
     * @param {number?} top - Optional. Specifies how many symbols to be returned in one query. Defaults to 100.
     * @param {number?} skip - Optional. Skips the first x symbols that would be returned. Can be used in conjunction with top to split the symbol universe across multiple requests. Defaults to 0.
     * @return {*}  {Promise<ApiResponse<SymbolUniverse>>}
     * @memberof SyftClient
     * @example
     * Making a request to get the top 10 symbols from the Symbol Universe, however skip 5 of them
     * ```ts
     * let symbols = await sdk.symbol.symbolUniverse(10, 5);
     * console.log(symbols);
     * ```
     */
    async symbolUniverse(top, skip) {
        const topSkip = this.getTopAndSkip('?', top, skip);
        const result = await api.get((0, url_1.buildFullyQualifiedUrl)(this._syftUrl, `symbols/${topSkip}`));
        return new ApiResponse_1.ApiResponse(result, new SymbolUniverse_1.SymbolUniverse(result.symbols));
    }
    /**
     * Returns an array of SymbolMetadata for symbols matching the provided pattern.
     * Searches can be on symbol name (in English, Japanese, or Kana), as well as
     * Japanese keywords and symbol codes.
     * @privateRemarks Source: https://confluence.tradestation.com/display/MI/Syft+API+Specification#SyftAPISpecification-GET/symbols/matches?pattern=%3Cpattern%3E
     * @privateRemarks Request: GET /symbols/matches?pattern=<pattern>
     *
     * @param {string} pattern - Required. The search pattern to use. Wildcards and other special characters are not supported. Syft will look up the given pattern in its precomputed match database and return the metadata for all symbols that contain the pattern as given.
     * @param {number?} top - Limits the returned symbols to a maximum number. Must be a positive integer in the range 1-1000 (inclusive). If no value is provided, if it is set to a non-number, or if a value greater than 1000 is provided, this parameter is set to 1000. If a value less than 1 is provided, this parameter is set to 1.
     * @param {number?} skip - Skips past a certain number of symbols. Must be a non-negative integer. The top limiter is applied after skip. If the value of skip is greater than or equal to the total number of symbols, no symbols will be returned. If no value is provided, if it is set to a non-number, or if a negative value is provided, this parameter is set to 0.
     * @param {boolean} jpeq - Boolean: when added, and set to true, Japanese Equity symbols (e.g. JP:7203-TS) will be returned. Defaults to true
     * @param {boolean} useq - Boolean: when added, and set to true, US Equity symbols (e.g. AAPL, MSFT) will be returned. Defaults to true
     * @param {boolean} supplemental - Boolean: when added, and set to true, supplemental symbols (e.g. bonds, indexes, forex, commodities) will be returned. Defaults to true
     * @param {boolean} industry - Boolean: when added, and set to true, Industry Symbols (e.g. 金属製品) will be returned, Defaults to true
     * @return {*}  {Promise<ApiResponse<SymbolMetadata[]>>}
     * @memberof SyftClient
     * @example
     * Performing a symbol search on 8698
     * ```ts
     *  let searchByPattern = await sdk.symbol.searchByPattern('8698');
     * ```
     * @example
     * Performing a symbol search on Apple for the top 10 US Equity symbols, but skipping the first result
     * ```ts
     * let searchByPattern = await sdk.symbol.searchByPattern('Apple', 10, 1, false, true, false, false);
     * ```
     * @example
     * Performing a symbol search for the firs industry symbol for metal
     * ```ts
     * let searchByPattern = await sdk.symbol.searchByPattern('金属', 1, 0, true, false, false, true);
     * console.log(searchByPattern);
     * ```
     */
    async symbolSearch(pattern, top, skip, jpeq = true, useq = true, supplemental = true, industry = true) {
        const topSkip = this.getTopAndSkip('&', top, skip);
        const assetType = this.getAssetType(jpeq, useq, supplemental, industry);
        const escapedPattern = encodeURIComponent(pattern);
        const results = await api.get((0, url_1.buildFullyQualifiedUrl)(this._syftUrl, `symbols/matches?pattern=${escapedPattern}${assetType}&language=${this._language}${topSkip}`));
        const symbolMetadata = results.symbols.map((symbol) => new SymbolMetadata_1.SymbolMetadata(symbol));
        return new ApiResponse_1.ApiResponse(results, symbolMetadata);
    }
    /**
     * Returns an array of SymbolMetadata for  supplemental symbols matching the provided pattern.
     * Searches can be on symbol name (in English, Japanese, or Kana), as well as
     * Japanese keywords and symbol codes.
     *
     * @privateRemarks Source: https://confluence.tradestation.com/display/MI/Syft+API+Specification#SyftAPISpecification-GET/symbols/matches/supplemental?pattern=%3Cpattern%3E
     * @privateRemarks Request: GET /symbols/matches/supplemental?pattern=<pattern>
     *
     * @param {string} pattern - Required. The search pattern to use. Wildcards and other special characters are not supported. Syft will look up the given pattern in its precomputed match database and return the metadata for all symbols that contain the pattern as given.
     * @param {number?} top - Limits the returned symbols to a maximum number. Must be a positive integer in the range 1-1000 (inclusive). If no value is provided, if it is set to a non-number, or if a value greater than 1000 is provided, this parameter is set to 1000. If a value less than 1 is provided, this parameter is set to 1.
     * @param {number?} skip - Skips past a certain number of symbols. Must be a non-negative integer. The top limiter is applied after skip. If the value of skip is greater than or equal to the total number of symbols, no symbols will be returned. If no value is provided, if it is set to a non-number, or if a negative value is provided, this parameter is set to 0.
     * @return {*}  {Promise<ApiResponse<SymbolMetadata[]>>}
     * @memberof SyftClient
     * @example
     * Searching for USDJPY
     * ```ts
     * let symbol = await sdk.supplementalSymbolSearch('USDJPY', 1, 0);
     * console.log(symbol);
     * ```
     */
    async supplementalSymbolSearch(pattern, top, skip) {
        const topSkip = this.getTopAndSkip('&', top, skip);
        const response = await api.get((0, url_1.buildFullyQualifiedUrl)(this._syftUrl, `symbols/matches/supplemental?pattern=${pattern}&language=${this._language}${topSkip}`));
        const symbols = response.symbols.map((symbol) => new SymbolMetadata_1.SymbolMetadata(symbol));
        return new ApiResponse_1.ApiResponse(response, symbols);
    }
    /**
     * Returns the SymbolMetadata for the specified symbol.
     * @privateRemarks Source: https://confluence.tradestation.com/display/MI/Syft+API+Specification#SyftAPISpecification-GET/symbols/%3Csymbol-id%3E
     * @privateRemarks Request: GET /symbols/<symbol-id>
     *
     * @param {string} symbol - Identifies the symbol for which to return metadata. This parameter must follow the symbol name format in TradeStation symbology. (US equity symbols are referred to by their codes, such as TSLA, whereas Japanese equity symbols are preceded by JP: and are followed by an exchange suffix, such as JP:7203-TS.) If the given ID contains any lower- case characters, it is converted to upper-case.
     * <br /><br />
     * A valid symbol ID consists of at least one of the following characters:
     * <br /><br />
     * - Upper- or lower-case Latin letters <br />
     * - Digits <br />
     * - Any of the following special characters: @:$.-
     * @return {*}  {Promise< ApiResponse<SymbolMetadata>>}
     * @memberof SyftClient
     * @example
     * ```ts
     * let symbol = await sdk.symbol.searchByCode('JP:$MNK');
     * console.log(symbol);
     * ```
     */
    async symbolMetadata(symbol) {
        const result = await api.get((0, url_1.buildFullyQualifiedUrl)(this._syftUrl, `symbols/${symbol}?language=${this._language}`));
        return new ApiResponse_1.ApiResponse(result, new SymbolMetadata_1.SymbolMetadata(result));
    }
    /**
     * Returns a list of symbol names for the specified index (e.g. NIKKEI225 or TOPIX)
     * @privateRemarks https://gitlab.com/monex-securities/insight/syft/-/wikis/Syft-API-Specification
     * @privateRemarks GET /list/<index>
     *
     * @param {string} symbol - Identifies the list to return symbol names for. A complete list of valid values can be found at via the getLists() command.
     * @return {*}  {Promise< ApiResponse<SymbolList | SymbolListError>>}
     * @memberof SyftClient
     * @example
     * Example request to get a list of symbols for NIKKEI225
     * ```ts
     *   let symbolNames = await sdk.symbol.getList('NIKKEI225');
     *   console.log(symbolNames);
     * ```
     */
    async getList(symbol) {
        const result = await api.get((0, url_1.buildFullyQualifiedUrl)(this._syftUrl, `list/${symbol}/`));
        if (result.errorCode) {
            return new ApiResponse_1.ApiResponse(result, new SymbolList_1.SymbolListError(result));
        }
        return new ApiResponse_1.ApiResponse(result, new SymbolList_1.SymbolList(result.list));
    }
    /**
     * Returns a list of the available indexes.
     * @privateRemarks https://gitlab.com/monex-securities/insight/syft/-/wikis/Syft-API-Specification
     * @privateRemarks GET /list/
     *
     * @return {*}  {Promise< ApiResponse<IndexList | IndexListError>>}
     * @memberof SyftClient
     * @example
     * ```ts
     *   let indexes = await sdk.symbol.getLists();
     *   console.log(indexes);
     * ```
     */
    async getLists() {
        const result = await api.get((0, url_1.buildFullyQualifiedUrl)(this._syftUrl, `list/`));
        if (result.errorCode) {
            return new ApiResponse_1.ApiResponse(result, new IndexList_1.IndexListError(result));
        }
        return new ApiResponse_1.ApiResponse(result, new IndexList_1.IndexList(result.lists));
    }
    /**
     * Returns an array of SymbolMetadata for the specified symbols. MAX 50 per request
     * @privateRemarks https://confluence.tradestation.com/display/MI/Syft+API+Specification#SyftAPISpecification-POST/symbols
     * @privateRemarks POST /symbols
     *
     * @param {string[]} symbols - An array list of up to 50 symbols. There is a limit of up to 50 symbols returned in one request.
     * @return {*}  {Promise<ApiResponse<Map<string, SymbolMetadata | SymbolMetadataError>>>}
     * @memberof SyftClient
     * @example
     * Request metadata for JP:8698-TS, USEQ, AAPL and JP:$MNK
     * ```ts
     *   let symbols = await sdk.symbolsMetadata(['JP:8698-TS', 'USEQ', 'AAPL', 'JP:$MNK']);
     *   console.log(symbols);
     * ```
     */
    async symbolsMetadata(symbols) {
        const result = await api.post((0, url_1.buildFullyQualifiedUrl)(this._syftUrl, `symbols?language=${this._language}`), JSON.stringify(symbols));
        const symbolsMap = new Map();
        result.symbols.forEach((symbolData) => {
            const symbol = Object.keys(symbolData)[0];
            const symbolvalues = symbolData[symbol];
            if (symbolvalues.errorCode) {
                symbolsMap.set(symbol, new SymbolMetadata_1.SymbolMetadataError(symbolvalues));
            }
            else {
                symbolsMap.set(symbol, new SymbolMetadata_1.SymbolMetadata(symbolvalues));
            }
        });
        return new ApiResponse_1.ApiResponse(result, symbolsMap);
    }
    getLanguageString(locale) {
        switch (locale) {
            case Locale_1.Locale.English:
                return 'en-US';
            case Locale_1.Locale.Katakana:
                return 'ja-kana-JP';
            case Locale_1.Locale.Japanese:
            default:
                return 'ja-JP';
        }
    }
    getTopAndSkip(prefix, top, skip) {
        if (top && skip) {
            return `${prefix}top=${top}&skip=${skip}`;
        }
        if (top) {
            return `${prefix}top=${top}`;
        }
        if (skip) {
            return `${prefix}skip=${skip}`;
        }
        return '';
    }
    getAssetType(isJpeq, isUseq, isSupplemental, isIndustry) {
        let returnString = '';
        if (isJpeq === false) {
            returnString = `${returnString}&jpeq=false`;
        }
        if (isUseq) {
            returnString = `${returnString}&useq=true`;
        }
        if (isSupplemental) {
            returnString = `${returnString}&supplemental=true`;
        }
        if (isIndustry) {
            returnString = `${returnString}&industry=true`;
        }
        return returnString;
    }
}
exports.SyftClient = SyftClient;
//# sourceMappingURL=syft.js.map